import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';

import { ActualSelected, Utils } from '../../../../../shared/utils';
import { MovimientoTarjetaCredito } from '../../../../../modules/finanzas/paymentcard/paymentcard.model';
import { PaymentCardService } from '../../../../../modules/finanzas/paymentcard/paymentcard.service';
import { PaymentCardDialogMovimientoTarjetaCreditoComponent } from '../../../../../modules/finanzas/paymentcard/tarjeta-credito/paymentcard-dialog-movimiento-tarjeta-credito.component';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import {
  CREDIT_CARD_MOVEMENT,
  expiryBadgeLabel,
  expiryBadgeVariant,
  maskCardNumber,
  PaymentCardRow,
} from '../../payment-card.utils';

interface CreditCardMovementLine {
  uid: string;
  date: Date | string;
  concept: string;
  meta: string;
  amount: number;
}

interface CreditCardMovementBlock {
  type: number;
  title: string;
  icon: string;
  singular: string;
  count: number;
  total: number;
  charges: number;
  lines: CreditCardMovementLine[];
}

const MAX_LINES = 5;

@Component({
  selector: 'app-credit-card-detail',
  templateUrl: './credit-card-detail.component.html',
  styleUrls: ['./credit-card-detail.component.scss'],
  standalone: false,
})
export class CreditCardDetailComponent implements OnChanges {
  @Input() row!: PaymentCardRow;
  @Output() changed = new EventEmitter<void>();
  @Output() ledger = new EventEmitter<void>();

  readonly year = ActualSelected.yearSelected;
  readonly maxLines = MAX_LINES;

  loading = true;
  error = false;
  blocks: CreditCardMovementBlock[] = [];
  disposed = 0;
  settled = 0;

  private loadedUid = '';

  constructor(
    private paymentCardService: PaymentCardService,
    private dialog: MatDialog,
    private confirm: ConfirmService,
    private notify: NotificationService,
    private tier: TierService,
  ) { }

  get maskedNumber(): string {
    return maskCardNumber(this.row && this.row.number);
  }

  get expiryVariant() {
    return expiryBadgeVariant(this.row.expiryStatus);
  }

  get expiryLabel(): string {
    return expiryBadgeLabel(this.row.expiring, this.row.expiryStatus);
  }

  get difference(): number {
    return Utils.RoundTwoDecimals(this.disposed - this.settled);
  }

  ngOnChanges(): void {
    if (!this.row || !this.row.uid) return;
    if (this.row.uid === this.loadedUid) return;
    this.loadedUid = this.row.uid;
    this.load(this.row.uid);
  }

  retry(): void {
    if (!this.row || !this.row.uid) return;
    this.load(this.row.uid);
  }

  add(block: CreditCardMovementBlock): void {
    this.openDialog(block);
  }

  manage(block: CreditCardMovementBlock): void {
    this.openDialog(block);
  }

  editLine(block: CreditCardMovementBlock, line: CreditCardMovementLine): void {
    this.openDialog(block, line.uid);
  }

  deleteLine(block: CreditCardMovementBlock, line: CreditCardMovementLine): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.confirm.open({
      title: `Eliminar ${block.singular}`,
      subtitle: [this.row.name, this.maskedNumber].filter(Boolean).join(' · ') || undefined,
      message: 'Se eliminará el movimiento de la tarjeta junto con su asiento contable. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.details(line),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando movimiento…',
      errorText: 'Ha ocurrido un error al eliminar',
      action: () => this.paymentCardService.movimientoTarjetaCreditoDelete(line.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Movimiento eliminado correctamente');
      this.changed.emit();
    });
  }

  trackByUid(_: number, line: CreditCardMovementLine): string {
    return line.uid;
  }

  trackByType(_: number, block: CreditCardMovementBlock): number {
    return block.type;
  }

  private openDialog(block: CreditCardMovementBlock, editUid?: string): void {
    if (!this.tier.requireAdvancedTier()) return;
    PaymentCardDialogMovimientoTarjetaCreditoComponent.open(this.dialog, { paymentCard: this.row, tipoMovimientoTarjetaCreditoId: block.type, editUid })
      .subscribe(changed => { if (changed) this.changed.emit(); });
  }

  private load(uid: string): void {
    this.loading = !this.blocks.length;
    this.error = false;

    Promise.all([
      this.paymentCardService.movimientoTarjetaCreditoList(uid, CREDIT_CARD_MOVEMENT.Disposicion),
      this.paymentCardService.movimientoTarjetaCreditoList(uid, CREDIT_CARD_MOVEMENT.Liquidacion),
    ])
      .then(([disposiciones, liquidaciones]) => {
        if (!this.row || uid !== this.row.uid) return;
        this.blocks = [
          this.buildBlock(CREDIT_CARD_MOVEMENT.Disposicion, 'Disposiciones', 'payment', 'disposición', disposiciones),
          this.buildBlock(CREDIT_CARD_MOVEMENT.Liquidacion, 'Liquidaciones', 'account_balance_wallet', 'liquidación', liquidaciones),
        ];
        this.disposed = this.blocks[0].total;
        this.settled = this.blocks[1].total;
      })
      .catch(() => { this.error = true; })
      .then(() => { this.loading = false; });
  }

  private buildBlock(type: number, title: string, icon: string, singular: string, items: MovimientoTarjetaCredito[]): CreditCardMovementBlock {
    const list = Array.isArray(items) ? items : [];
    const lines = list.map(item => ({
      uid: item.uid,
      date: item.fecha,
      concept: item.concepto || 'Sin concepto',
      meta: [
        item.activitiesName || '',
        item.comisiones ? 'Comisiones ' + Utils.FormatMoney(item.comisiones) + ' €' : '',
        item.intereses ? 'Intereses ' + Utils.FormatMoney(item.intereses) + ' €' : '',
      ].filter(Boolean).join(' · '),
      amount: Number(item.importe) || 0,
    }));
    return {
      type,
      title,
      icon,
      singular,
      count: lines.length,
      total: Utils.RoundTwoDecimals(lines.reduce((acc, line) => acc + line.amount, 0)),
      charges: Utils.RoundTwoDecimals(list.reduce((acc, m) => acc + (Number(m.comisiones) || 0) + (Number(m.intereses) || 0), 0)),
      lines: lines.slice(0, MAX_LINES),
    };
  }

  private details(line: CreditCardMovementLine): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (line.date) details.push({ label: 'Fecha', value: formatDate(line.date, 'dd/MM/yyyy', 'es-ES') });
    details.push({ label: 'Concepto', value: line.concept });
    if (line.meta) details.push({ label: 'Detalle', value: line.meta });
    details.push({ label: 'Importe', value: Utils.FormatMoney(line.amount) + ' €' });
    return details;
  }
}
